import { createFileRoute, Outlet, useLocation, useNavigate } from "@tanstack/react-router"
import { useAuth } from "hooks/use-auth"
import { useUserType } from "hooks/use-user-type"
import { useEffect } from "react"
import { PaymentRequiredModal } from "@/components/billing/payment-required-modal"
import { ProfessionalChatWidget } from "@/components/chat/professional-chat-widget"
import { PageLoader } from "@/components/page-loader"
import { trpc } from "@/lib/trpc"

const PAYWALL_EXEMPT_PATHS = new Set(["/professional/billing"])
const FREE_CLIENT_ALLOWANCE = 3

export const Route = createFileRoute("/_authed/_professional")({
  component: ProfessionalLayout,
})

function ProfessionalLayout() {
  const { isAuthenticated, isLoading } = useAuth()
  const { isProfessional } = useUserType()
  const navigate = useNavigate()
  const pathname = useLocation({ select: (location) => location.pathname })

  const isPaywallDebug = window?.location?.search?.includes("debugPaywall=1") ?? false

  const {
    data: subscription,
    isLoading: isCheckingSubscription,
    error: subscriptionError,
    status: subscriptionQueryStatus,
    isFetching: subscriptionIsFetching,
    refetch: refetchSubscription,
  } = trpc.stripe.getSubscriptionStatus.useQuery(undefined, {
    enabled: isAuthenticated && isProfessional,
    retry: 0,
  })

  const { data: estates = [] } = trpc.estates.list.useQuery(undefined, {
    enabled: isAuthenticated && isProfessional,
  })

  const clientCount = estates.length

  const hasActiveSubscription =
    subscription?.status === "active" || subscription?.status === "trialing"

  useEffect(() => {
    if (!isPaywallDebug) return
    // One clean line you can grep in console
    // biome-ignore lint/suspicious/noConsole: stripe debug logging
    console.info("[paywall] layout", {
      isLoading,
      isAuthenticated,
      isProfessional,
      clientCount,
      freeClientAllowance: FREE_CLIENT_ALLOWANCE,
      exceedsAllowance: clientCount > FREE_CLIENT_ALLOWANCE,
      subscriptionQueryStatus,
      subscriptionIsFetching,
      subscription,
      hasActiveSubscription,
      subscriptionError: subscriptionError
        ? {
            message: subscriptionError.message,
            code: subscriptionError.data?.code,
            httpStatus: subscriptionError.data?.httpStatus,
            path: subscriptionError.data?.path,
          }
        : null,
    })
  }, [
    isPaywallDebug,
    isLoading,
    isAuthenticated,
    isProfessional,
    clientCount,
    subscriptionQueryStatus,
    subscriptionIsFetching,
    subscription,
    hasActiveSubscription,
    subscriptionError,
  ])

  // If you come back from Stripe with checkout=success, force a refetch
  useEffect(() => {
    const checkout = new URLSearchParams(window.location.search).get("checkout")
    if (checkout === "success") {
      if (isPaywallDebug) {
        // biome-ignore lint/suspicious/noConsole: stripe debug logging
        console.info("[paywall] checkout=success detected, refetching subscription status…")
      }
      refetchSubscription()
    }
  }, [isPaywallDebug, refetchSubscription])

  useEffect(() => {
    if (!isLoading && isAuthenticated && !isProfessional) {
      navigate({ to: "/personal/dashboard", replace: true })
    }
  }, [isAuthenticated, isLoading, isProfessional, navigate])

  if (isLoading || isCheckingSubscription) return <PageLoader />
  if (!isAuthenticated || !isProfessional) return null

  const mustShowPaywall =
    !PAYWALL_EXEMPT_PATHS.has(pathname) &&
    clientCount > FREE_CLIENT_ALLOWANCE &&
    !hasActiveSubscription

  return (
    <>
      {mustShowPaywall && <PaymentRequiredModal />}
      <Outlet />
      <ProfessionalChatWidget />
    </>
  )
}
